import { useMemo } from 'react'
import type { Contact } from '../types/contact.types'
import type { EnumOption } from '../types/enum.types'
import type { CatalogItem } from '../types/adapter.types'
import type { ContactState } from './contactGridUtils'
import { STATE_SHADOW_STYLE, formatPhoneForGrid, formatSocialForGrid } from './contactGridUtils'
import { getColorForIndex } from '../utils/tagColors'
import { ContactRowActions } from './ContactRowActions'
import { useTranslations } from '../providers/ContactServiceProvider'

interface ResponsiveContactCardProps {
  contact: Contact
  state: ContactState
  mode: 'edit' | 'view'
  useForOptions?: EnumOption[]
  phoneCategoryOptions?: EnumOption[]
  socialNetworkTypes?: CatalogItem[]
  onEdit: (id: string) => void
  onView: (id: string) => void
  onDelete: (id: string) => void
  onRestore: (id: string) => void
}

export function ResponsiveContactCard({
  contact,
  state,
  mode,
  useForOptions,
  phoneCategoryOptions,
  socialNetworkTypes,
  onEdit,
  onView,
  onDelete,
  onRestore,
}: ResponsiveContactCardProps) {
  const t = useTranslations()
  const stateStyle = state ? STATE_SHADOW_STYLE[state] : ''
  const isDeleted = state === 'deleted'
  const showClassification = (useForOptions?.length ?? 0) > 1

  const socialNetworkLabelMap = useMemo(() => {
    if (!socialNetworkTypes) return undefined
    return new Map(socialNetworkTypes.map((snt) => [snt.id, snt.name]))
  }, [socialNetworkTypes])

  const categoryLabelMap = useMemo(() => {
    if (!phoneCategoryOptions) return undefined
    return new Map(phoneCategoryOptions.map((o) => [o.value, o.label]))
  }, [phoneCategoryOptions])

  return (
    <div className={`rounded-lg bg-bg-primary p-4 shadow-xs border border-border-secondary dark:bg-dark-bg-primary dark:border-dark-border-primary ${stateStyle} ${isDeleted ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium text-content-primary dark:text-dark-content-primary ${isDeleted ? 'line-through' : ''}`}>
            {contact.name}
          </p>
          {showClassification && contact.useFor.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {contact.useFor.map((useForValue) => {
                const option = useForOptions!.find((o) => o.value === useForValue)
                const colorIndex = useForOptions!.findIndex((o) => o.value === useForValue)
                const color = getColorForIndex(colorIndex >= 0 ? colorIndex : 0)
                return (
                  <span
                    key={useForValue}
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${color.bg} ${color.text} ${color.darkBg} ${color.darkText}`}
                  >
                    {option?.label ?? String(useForValue)}
                  </span>
                )
              })}
            </div>
          )}
          {contact.emails.length > 0 && (
            <div className="mt-1 flex items-center gap-1">
              <span className="inline-block max-w-[180px] truncate rounded-full bg-slate-100 px-2 py-0.5 text-xs text-content-primary dark:bg-slate-800 dark:text-dark-content-primary">
                {contact.emails[0]}
              </span>
              {contact.emails.length > 1 && (
                <span className="inline-block rounded-full bg-primary-100 px-1.5 py-0.5 text-xs font-medium text-primary-600">
                  +{contact.emails.length - 1}
                </span>
              )}
            </div>
          )}
          {contact.phones.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {contact.phones.map((phone, i) => (
                <p key={i} className="text-xs text-content-secondary dark:text-dark-content-secondary">
                  {formatPhoneForGrid(phone, categoryLabelMap)}
                </p>
              ))}
            </div>
          )}
          {contact.socialNetworks.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {contact.socialNetworks.map((sn, i) => (
                <p key={i} className="text-xs text-content-tertiary dark:text-dark-content-tertiary">
                  {formatSocialForGrid(sn, socialNetworkLabelMap, t.grid.socialNetworkNotEnabled)}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className="ml-4 flex-shrink-0">
          {contact.id != null && (
            <ContactRowActions
              contactId={contact.id}
              mode={mode}
              isPendingDelete={isDeleted}
              onEdit={onEdit}
              onView={onView}
              onDelete={onDelete}
              onRestore={onRestore}
            />
          )}
        </div>
      </div>
    </div>
  )
}
